import React, { forwardRef, useEffect, useState } from 'react';
import { cn } from '../../../lib/cn';
import { Tooltip } from './Tooltip';
import { rand } from './platform';

/**
 * FilterRail, the sticky 256px desktop replacement for the mobile filter sheet.
 *
 * CSS: `desktop/system.css` `.dsk-filter-rail`, `.dsk-facet`, `.dsk-facet__head`,
 * `.dsk-facet__count`, `.dsk-facet__option`, `.dsk-check`, plus `.dsk-hint`,
 * `.dsk-input` and `.dsk-field__foot` for the price control.
 * Behaviour: PriceHistogram ports the range/dimming logic and BEHAVIOUR #3
 * (`[data-tooltip]` / `[data-tooltip-meta]`) via the `Tooltip` component.
 *
 * Stickiness, the max height and the internal scroll are all in
 * `.dsk-filter-rail`; nothing is set here.
 *
 * KNOWN GAP, reported rather than hidden: the price histogram and its
 * dual-thumb range have no classes in `desktop/system.css`. Upstream they are
 * page-local `.pg-histo` / `.pg-range` in `desktop/templates/marketplace.html`
 * and were never promoted to the layer. The six classes this file emits are
 * named to mirror the mobile track's already-promoted `.ds-histogram` family so
 * promoting them upstream is a rename, not a redesign:
 *   .dsk-histogram  __bars  __bar  __track  __fill  __input   (+ .is-in-range)
 */

/** The rail itself. */
export const FilterRail = forwardRef(function FilterRail(
  { label = 'Filters', children, className, ...rest },
  ref,
) {
  return (
    <aside ref={ref} className={cn('dsk-filter-rail', className)} aria-label={label} {...rest}>
      {children}
    </aside>
  );
});

/**
 * Facet, one filter section. `count` is the number of filters applied inside
 * it and renders as the accent `.dsk-facet__count` badge, because "you have
 * applied something here" is state, not a command.
 */
export const Facet = forwardRef(function Facet(
  { title, count, action, children, className, ...rest },
  ref,
) {
  return (
    <section ref={ref} className={cn('dsk-facet', className)} {...rest}>
      <div className="dsk-facet__head">
        <span>{title}</span>
        {count ? (
          <span className="dsk-facet__count">
            {count}
            <span className="dsk-sr-only"> filters applied</span>
          </span>
        ) : (
          action || null
        )}
      </div>
      {children}
    </section>
  );
});

/**
 * FacetOption, a checkbox facet row. The whole row is the hit target because it
 * is a <label> wrapping the real `<input type="checkbox">`, so form semantics,
 * keyboard operation and the focus ring all come for free.
 */
export const FacetOption = forwardRef(function FacetOption(
  { label, count, checked = false, onChange, disabled = false, className, ...rest },
  ref,
) {
  return (
    <label className={cn('dsk-facet__option', className)} {...rest}>
      <input
        ref={ref}
        type="checkbox"
        className="dsk-check"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange && onChange(event.target.checked, event)}
      />
      <span>{label}</span>
      {count != null ? (
        // Layout only: pushes the count to the trailing edge of the flex row.
        <span className="dsk-hint" style={{ marginLeft: 'auto' }}>
          {count}
        </span>
      ) : null}
    </label>
  );
});

/* Strip the R and the comma grouping back off an edited price field. */
function parsePrice(text) {
  const digits = String(text).replace(/[^\d.]/g, '');
  const value = Number.parseFloat(digits);
  return Number.isFinite(value) ? value : null;
}

/**
 * PriceHistogram, the price facet: a distribution the user can read and a
 * dual-thumb range they can drag.
 *
 * Ported from the marketplace template's price filter, including its two rules:
 *
 *   DIMMING. Bars outside the range keep their bar but drop to the light /
 *   disabled treatment, so the distribution stays readable while the selection
 *   stays obvious. In-range bars go accent, because range membership is
 *   selection state.
 *
 *   TOOLTIP. Each bar carries its count and its band through `Tooltip`
 *   (behaviour #3). Bars are presentational: they are not focusable, and the
 *   whole distribution is instead named once on `.dsk-histogram__bars` via
 *   `role="img"` + `aria-label`, so no information is pointer-only. The two
 *   range inputs are the real controls and each carries its own label and
 *   `aria-valuetext` in Rands.
 *
 * @param {object[]} buckets  [{ from, to, count }] in ascending order
 */
export const PriceHistogram = forwardRef(function PriceHistogram(
  {
    buckets = [],
    min = 0,
    max = 20000,
    step = 50,
    value,
    onChange,
    format = rand,
    showInputs = true,
    minLabel = 'Minimum price',
    maxLabel = 'Maximum price',
    distributionLabel,
    className,
    ...rest
  },
  ref,
) {
  const [low, high] = value || [min, max];
  const [lowText, setLowText] = useState(() => format(low));
  const [highText, setHighText] = useState(() => format(high));

  useEffect(() => {
    setLowText(format(low));
    setHighText(format(high));
    // `format` is stable in practice; re-running on it would fight the caller.
    // eslint-disable-next-line
  }, [low, high]);

  const span = Math.max(1, max - min);
  const pct = (v) => ((v - min) / span) * 100;
  const tallest = buckets.reduce((acc, bucket) => Math.max(acc, bucket.count || 0), 0) || 1;

  const emit = (nextLow, nextHigh) => {
    if (onChange) onChange([nextLow, nextHigh]);
  };

  /* Keep the thumbs from crossing, exactly as the reference range does. */
  const onLowInput = (event) => {
    const next = Number(event.target.value);
    emit(next, Math.max(next, high));
  };
  const onHighInput = (event) => {
    const next = Number(event.target.value);
    emit(Math.min(next, low), next);
  };

  const commitLowText = () => {
    const parsed = parsePrice(lowText);
    if (parsed == null) {
      setLowText(format(low));
      return;
    }
    const clamped = Math.min(Math.max(parsed, min), high);
    emit(clamped, high);
  };
  const commitHighText = () => {
    const parsed = parsePrice(highText);
    if (parsed == null) {
      setHighText(format(high));
      return;
    }
    const clamped = Math.max(Math.min(parsed, max), low);
    emit(low, clamped);
  };

  const summary =
    distributionLabel ||
    `Price distribution across ${buckets.length} bands, ${format(min)} to ${format(max)}`;

  return (
    <div ref={ref} className={cn('dsk-histogram', className)} {...rest}>
      <div className="dsk-histogram__bars" role="img" aria-label={summary}>
        {buckets.map((bucket) => {
          /* A band is in range if it overlaps the selection at all, which is
             what the reference's `to > a && from < b` test says. */
          const inRange = bucket.to > low && bucket.from < high;
          return (
            <Tooltip
              key={`${bucket.from}-${bucket.to}`}
              value={`${bucket.count} cards`}
              meta={`${format(bucket.from)} to ${format(bucket.to)}`}
            >
              <span
                className={cn('dsk-histogram__bar', inRange && 'is-in-range')}
                /* Geometry only: the bar's height IS the datum. */
                style={{ height: `${Math.max(4, ((bucket.count || 0) / tallest) * 100)}%` }}
              />
            </Tooltip>
          );
        })}
      </div>

      <div className="dsk-histogram__track">
        <div
          className="dsk-histogram__fill"
          /* Geometry only. */
          style={{ left: `${pct(low)}%`, right: `${100 - pct(high)}%` }}
        />
        <input
          type="range"
          className="dsk-histogram__input"
          min={min}
          max={max}
          step={step}
          value={low}
          onChange={onLowInput}
          aria-label={minLabel}
          aria-valuetext={format(low)}
        />
        <input
          type="range"
          className="dsk-histogram__input"
          min={min}
          max={max}
          step={step}
          value={high}
          onChange={onHighInput}
          aria-label={maxLabel}
          aria-valuetext={format(high)}
        />
      </div>

      {showInputs ? (
        <div className="dsk-field__foot">
          <input
            className="dsk-input"
            value={lowText}
            onChange={(event) => setLowText(event.target.value)}
            onBlur={commitLowText}
            onKeyDown={(event) => {
              if (event.key === 'Enter') commitLowText();
            }}
            aria-label={`${minLabel}, editable`}
            /* Layout only: without min-width:0 a flex item refuses to shrink
               below its content width and the second field leaves the rail. */
            style={{ flex: '1 1 0', minWidth: 0 }}
          />
          <span className="dsk-hint">to</span>
          <input
            className="dsk-input"
            value={highText}
            onChange={(event) => setHighText(event.target.value)}
            onBlur={commitHighText}
            onKeyDown={(event) => {
              if (event.key === 'Enter') commitHighText();
            }}
            aria-label={`${maxLabel}, editable`}
            style={{ flex: '1 1 0', minWidth: 0 }}
          />
        </div>
      ) : null}
    </div>
  );
});

export default FilterRail;
